import React, { useRef, useState } from 'react';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { allReviews, Review } from '../../constants/opinions-data';

export function ReviewCard({ slide }: { slide: Review }) {
	return (
		<div className="bg-white rounded-3xl p-7 shadow-soft hover:shadow-soft-lg transition-all duration-300 flex flex-col h-full border border-gray-100/80">
			<div className="flex items-center gap-1 mb-4">
				{[...Array(slide.rate)].map((_, i) => (
					<StarIcon key={i} sx={{ fontSize: 18, color: '#FBBF24' }} />
				))}
			</div>

			<div className="relative flex-grow mb-6">
				<FormatQuoteIcon
					className="absolute -top-1 -left-1 text-primary-100"
					sx={{ fontSize: 40 }}
				/>
				<p className="text-gray-600 leading-relaxed font-montserrat-custom-medium text-sm relative z-10 pl-6">
					{slide.description}
				</p>
			</div>

			<div className="flex items-center gap-3 pt-4 border-t border-gray-100">
				<div
					className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
					style={{ backgroundColor: slide.avatar_color }}
				>
					{slide.avatar}
				</div>
				<div>
					<div className="font-bold text-gray-900 text-sm">{slide.person}</div>
					<div className="text-gray-400 text-xs font-montserrat-custom-medium">{slide.date}</div>
				</div>
			</div>
		</div>
	);
}

export function ReviewsSlider({ reviews }: { reviews: Review[] }) {
	const [current, setCurrent] = useState(0);
	const VISIBLE = 3;
	const total = reviews.length;

	const prev = () => setCurrent(c => Math.max(0, c - 1));
	const next = () => setCurrent(c => Math.min(total - VISIBLE, c + 1));

	const canPrev = current > 0;
	const canNext = current < total - VISIBLE;

	return (
		<div className="relative">
			<div className="overflow-hidden">
				<div
					className="flex transition-transform duration-500 ease-in-out gap-6"
					style={{ transform: `translateX(calc(-${current} * (100% / ${VISIBLE} + 8px)))` }}
				>
					{reviews.map((review, index) => (
						<div
							key={index}
							className="flex-shrink-0"
							style={{ width: `calc((100% - ${(VISIBLE - 1) * 24}px) / ${VISIBLE})` }}
						>
							<ReviewCard slide={review} />
						</div>
					))}
				</div>
			</div>

			{(canPrev || canNext) && (
				<div className="flex items-center justify-center gap-4 mt-8">
					<button
						onClick={prev}
						disabled={!canPrev}
						className="w-10 h-10 rounded-full bg-white shadow-soft flex items-center justify-center text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
					>
						<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
					</button>
					<div className="flex gap-2">
						{Array.from({ length: total - VISIBLE + 1 }).map((_, i) => (
							<button
								key={i}
								onClick={() => setCurrent(i)}
								className={`w-2 h-2 rounded-full transition-all duration-200 ${i === current ? 'bg-primary-500 w-6' : 'bg-gray-300'}`}
							/>
						))}
					</div>
					<button
						onClick={next}
						disabled={!canNext}
						className="w-10 h-10 rounded-full bg-white shadow-soft flex items-center justify-center text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
					>
						<ArrowForwardIosIcon sx={{ fontSize: 16 }} />
					</button>
				</div>
			)}
		</div>
	);
}

export default function OpinionPage() {
	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 lg:pt-24 pb-16">
			<div className="text-center mb-12">
				<span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-bold mb-4">
					OPINIE
				</span>
				<h2 className="font-bold-custom text-4xl lg:text-5xl text-gray-900">
					Co mówią o mnie <span className="text-gradient">moje podopieczne?</span>
				</h2>
			</div>

			<div className="hidden lg:block">
				<ReviewsSlider reviews={allReviews} />
			</div>

			<div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6">
				{allReviews.map((slide, index) => (
					<ReviewCard key={index} slide={slide} />
				))}
			</div>

			<div className="text-center mt-10">
				<a
					href="https://www.google.com/search?q=Trener+Personalny+-+Amelia+Pa%C5%82czy%C5%84ska+Opinie&rflfq=1&num=20&rldimm=11180015413868640070&tbm=lcl&hl=pl-PL#lkt=LocalPoiReviews"
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center gap-2 px-7 py-3.5 bg-white border border-gray-200 rounded-full text-gray-700 font-bold shadow-soft hover:shadow-soft-lg hover:scale-105 transition-all duration-300 text-sm"
				>
					<StarIcon sx={{ fontSize: 18, color: '#FBBF24' }} />
					Zostaw opinię w Google
					<OpenInNewIcon sx={{ fontSize: 16 }} className="text-gray-400" />
				</a>
			</div>
		</div>
	);
}
